import { useState, FormEvent } from 'react'
import { supabase } from '../../utils/supabase'
import { getConfiguredApiBase } from '../../api/client'
import Button from '../ui/Button'

const META_DOMAIN = 'meta.com'

function isValidMetaEmail(email: string): boolean {
  const trimmed = email.trim().toLowerCase()
  const parts = trimmed.split('@')
  return parts.length === 2 && parts[0].length > 0 && parts[1] === META_DOMAIN
}

export default function EmailLoginForm() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isSignUp, setIsSignUp] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    const trimmedEmail = email.trim().toLowerCase()

    if (!isValidMetaEmail(trimmedEmail)) {
      setError('Only @meta.com email addresses are allowed')
      return
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }

    setLoading(true)

    try {
      if (isSignUp) {
        // Extract display name from email (part before @)
        const displayName = trimmedEmail.split('@')[0]

        if (import.meta.env.VITE_SKIP_EMAIL_CONFIRMATION === 'true') {
          // Bypass email confirmation: create user via backend admin API
          const res = await fetch(`${getConfiguredApiBase()}/auth/signup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: trimmedEmail, password }),
          })

          if (!res.ok) {
            const data = await res.json().catch(() => ({}))
            setError(data.detail || 'Signup failed')
            return
          }

          // User created with email pre-confirmed — sign in immediately
          const { error: signInError } = await supabase.auth.signInWithPassword({
            email: trimmedEmail,
            password,
          })

          if (signInError) {
            setError(signInError.message)
            return
          }

          // Success - AuthContext will handle the session update
        } else {
          // Standard flow: Supabase sends a confirmation email
          const { error: signUpError } = await supabase.auth.signUp({
            email: trimmedEmail,
            password,
            options: {
              emailRedirectTo: window.location.origin,
              data: {
                display_name: displayName,
                full_name: displayName,
              },
            },
          })

          if (signUpError) {
            setError(signUpError.message)
            return
          }

          setSuccess(true)
        }
      } else {
        // Sign in with email + password
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: trimmedEmail,
          password,
        })

        if (signInError) {
          if (signInError.message.includes('Invalid login credentials')) {
            setError('Invalid email or password')
          } else if (signInError.message.includes('Email not confirmed')) {
            setError('Please check your email and confirm your account first')
          } else {
            setError(signInError.message)
          }
          return
        }

        // Success - AuthContext will handle the session update
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed')
    } finally {
      setLoading(false)
    }
  }

  if (success && isSignUp) {
    return (
      <div className="text-center p-4">
        <div className="text-green-400 mb-2">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">Check your email</h3>
        <p className="text-text-secondary text-sm">
          We sent a confirmation email to <span className="text-foreground">{email}</span>
        </p>
        <p className="text-text-muted text-xs mt-2">Click the link in the email to activate your account</p>
        <button
          onClick={() => {
            setSuccess(false)
            setIsSignUp(false)
            setPassword('')
          }}
          className="mt-4 text-sm text-primary hover:underline"
        >
          Back to Sign In
        </button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="email" className="block text-sm text-text-secondary mb-1">
          Work Email
        </label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@meta.com"
          className="w-full px-3 py-2 bg-surface-dark rounded border border-white/10 text-sm focus:outline-none focus:border-primary"
          required
          disabled={loading}
        />
        <p className="text-xs text-text-muted mt-1">Only @meta.com addresses are allowed</p>
      </div>

      <div>
        <label htmlFor="password" className="block text-sm text-text-secondary mb-1">
          Password
        </label>
        <input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={isSignUp ? 'Create a password (min 6 characters)' : 'Enter your password'}
          className="w-full px-3 py-2 bg-surface-dark rounded border border-white/10 text-sm focus:outline-none focus:border-primary"
          required
          disabled={loading}
          minLength={6}
        />
      </div>

      {error && (
        <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      <Button
        type="submit"
        loading={loading}
        disabled={loading || !email.trim() || password.length < 6}
        className="w-full"
      >
        {loading ? (isSignUp ? 'Creating account...' : 'Signing in...') : (isSignUp ? 'Create Account' : 'Sign In')}
      </Button>

      <div className="text-center">
        <button
          type="button"
          onClick={() => {
            setIsSignUp(!isSignUp)
            setError(null)
          }}
          className="text-sm text-text-secondary hover:text-foreground transition-colors"
        >
          {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
        </button>
      </div>
    </form>
  )
}
